import React from 'react';

import { Divider, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  Column,
  Icon,
  Row,
  Space,
  Text,
  TooltipPlacement,
} from 'farmhub-ui-core';

import { SelectedEntityDomain } from 'api';
import { EditableTagGroup, ImgSkeleton } from 'components';
import {
  EntityRoleOutlineIcon,
  WebsiteOutlineIcon,
  TimeWaitingIcon,
} from 'components/Icon';
import { theme } from 'styled';
import {
  DOMAIN_STATUS,
  formatWebsite,
  handleRedirect,
  IMAGETYPE,
  USER_ROLE,
} from 'utils';
import {
  formatImageUrl,
  genCensorshipStatus,
  getEntityInfo,
  handleUpdateOptions,
} from 'utils/function';

import {
  DomainWrapperDesktop,
  EntityTypeIconStylesDesktop,
  ImgDesktop,
  ImgWrapperDesktop,
  InfoCardDesktop,
  TextWrapperDesktop,
  WrapperDesktop,
} from './styles';

interface AccountDesktopProps {
  item: any;
  itemList?: any;
  handleChangeAccountList?: (e: any[]) => void;
  onSelected: (value: SelectedEntityDomain) => void;
}

// 0: 'user', 1: 'farm', 2: 'store', 3: 'collaborator', 4: 'warehouse', 5: 'transport', 6: 'volunteer'
const AccountDesktop = ({
  item,
  itemList,
  onSelected,
  handleChangeAccountList,
}: AccountDesktopProps): JSX.Element => (
  <WrapperDesktop
    onClick={() => {
      onSelected({
        adminDomain: item.adminDomain,
        id: item.id,
        isOpenNewTab: item.isOpenNewTab,
        role: item.role,
        status: item.status,
        type: item.type,
        domainStatus: item.domainStatus,
        employeeStatus: item.employeeStatus,
        isSkipCheckAccess: item.isSkipCheckAccess,
      });
    }}
  >
    <InfoCardDesktop>
      <Row styles={{ position: 'relative', gap: 12, zIndex: 1 }} width="100%">
        {item.img ? (
          <ImgWrapperDesktop>
            <ImgDesktop
              alt="img"
              src={formatImageUrl(item.avatar, IMAGETYPE.AVATAR.xxs)}
            />
            {item.type && (
              <Tooltip
                placement={TooltipPlacement.Top}
                title={getEntityInfo(item.type).name}
              >
                <Icon
                  height="20px"
                  styles={EntityTypeIconStylesDesktop}
                  width="20px"
                >
                  {getEntityInfo(item.type).icon}
                </Icon>
              </Tooltip>
            )}
          </ImgWrapperDesktop>
        ) : (
          <ImgSkeleton height={64} width={64} />
        )}
        <TextWrapperDesktop>
          <Row justifyContent="space-between" styles={{ gap: 12 }} width="100%">
            <Text
              color="primary"
              lineHeight="18px"
              lineNumber={2}
              weight="bold"
            >
              {item.fullName}
            </Text>
            <Row styles={{ gap: 8 }} width="fit-content">
              <EditableTagGroup
                editAble={
                  item.role != USER_ROLE.EMPLOYEE &&
                  item.role != USER_ROLE.MANAGER
                }
                id={item.id}
                maxWidth="180px"
                name={item.displayName}
                tags={item.entityOptions?.map((e: any) => e.option)}
                type="ENTITY"
                onAfterSubmit={(options, deletedOptionIDs, typeOptions) =>
                  handleUpdateOptions(
                    itemList,
                    // e => dispatch(uploadEntityList(e)),
                    handleChangeAccountList,
                    item.id,
                    options,
                    deletedOptionIDs,
                    typeOptions,
                    'entityOptions'
                  )
                }
              />
              <Row width="fit-content">
                <Tooltip title={genCensorshipStatus(item.status).name}>
                  <Row styles={{ 'flex-shrink': 0 }} width="fit-content">
                    {genCensorshipStatus(item.status, 16).icon}
                  </Row>
                </Tooltip>
              </Row>
            </Row>
          </Row>

          <Space height={6} />
          <Row justifyContent="space-between" styles={{ gap: 12 }} width="100%">
            <Row width="fit-content">
              <Row styles={{ gap: 6, flexShrink: 0 }} width="fit-content">
                <Column alignItems="flex-end" width="fit-content">
                  <EntityRoleOutlineIcon
                    color={theme.colors.text.secondary}
                    height={12}
                    styles={{ marginTop: '3px' }}
                    width={12}
                  />
                </Column>
                <Tooltip title={item?.employeeEntityRole?.name}>
                  <Text color="secondary" lineNumber={1} size="xxs">
                    {item?.employeeEntityRole?.name}
                  </Text>
                </Tooltip>
              </Row>
              <Divider type="vertical" />
              <Row alignItems="center" styles={{ gap: 6 }} width="fit-content">
                <Column width="fit-content">
                  <WebsiteOutlineIcon color="#52525A" height={12} width={12} />
                </Column>

                {item.domain ? (
                  <>
                    <DomainWrapperDesktop alignItems="center">
                      <Tooltip title={item.domain}>
                        <Text
                          lineNumber={1}
                          size="xxs"
                          style={{
                            cursor: 'pointer',
                            color: theme.colors.success,
                          }}
                          onClick={e => {
                            item?.domainStatus == DOMAIN_STATUS.ACTIVATE &&
                              handleRedirect(item.domain);
                            e.stopPropagation();
                          }}
                        >
                          {formatWebsite(item.domain)}
                        </Text>
                      </Tooltip>

                      <Space width={6} />
                      {item?.domainStatus == DOMAIN_STATUS.ACTIVATE ? (
                        <Column width="fit-content">
                          <Tooltip title="Đã kích hoạt">
                            <CheckCircleOutlined
                              className="check-out-line"
                              style={{
                                color: theme.colors.success,
                              }}
                            />
                          </Tooltip>
                        </Column>
                      ) : (
                        <Tooltip title="Chưa kích hoạt">
                          <Column width="fit-content">
                            <TimeWaitingIcon
                              className="check-out-line"
                              height={12}
                              width={12}
                            />
                          </Column>
                        </Tooltip>
                      )}
                    </DomainWrapperDesktop>
                  </>
                ) : item?.website ? (
                  <Tooltip title={formatWebsite(item.suggestDomain)}>
                    <Column width="fit-content">
                      <Text
                        lineNumber={1}
                        size="xxs"
                        style={{
                          cursor: 'pointer',
                          color: theme.colors.success,
                        }}
                        onClick={e => {
                          handleRedirect(item.suggestDomain);
                          e.stopPropagation();
                        }}
                      >
                        {formatWebsite(item.suggestDomain)}
                        {/* Website hệ thống */}
                      </Text>
                    </Column>
                  </Tooltip>
                ) : (
                  <Text
                    color="secondary"
                    size="xxs"
                    styles={{ fontStyle: 'italic' }}
                  >
                    Chưa thiết lập
                  </Text>
                )}
              </Row>
            </Row>
            <Text
              size="xxs"
              styles={{
                fontStyle: 'italic',
                color: '#52525A',
                flexShrink: 0,
              }}
            >
              Chưa truy cập
            </Text>
          </Row>
        </TextWrapperDesktop>
      </Row>
    </InfoCardDesktop>
  </WrapperDesktop>
);

export default AccountDesktop;
